import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { companyResourcesPath, newCompanyResourcePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface ResourceType {
  id: number
  name: string
}

interface ResourceForm {
  name: string
  resource_type_id: number | null
  notes: string
  build: boolean
  status: number
}

interface Props {
  resource: ResourceForm
  resource_types: ResourceType[]
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Bios & Flyers", href: companyResourcesPath() },
  { title: "Add Resource", href: newCompanyResourcePath() },
]

export default function New({ resource, resource_types, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: resource.name ?? "",
    resource_type_id: resource.resource_type_id ?? "",
    notes: resource.notes ?? "",
    build: resource.build ?? false,
    status: resource.status ?? 0,
    file: null as File | null,
  })

  transform((data) => ({ resource: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(companyResourcesPath(), {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Resource" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Resource</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Type</label>
            <select
              value={data.resource_type_id}
              onChange={(e) =>
                setData(
                  "resource_type_id",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              required
              className={inputClass}
            >
              <option value="">Select a type...</option>
              {resource_types.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              required
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={5}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Status</label>
            <select
              value={data.status}
              onChange={(e) => setData("status", Number(e.target.value))}
              className={inputClass}
            >
              <option value={0}>Active</option>
              <option value={1}>Inactive</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="build"
              checked={!!data.build}
              onChange={(e) => setData("build", e.target.checked)}
              className="text-hpi-active-blue h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="build" className="text-hpi-dark-gray text-sm">
              Include in build list
            </label>
          </div>

          <div>
            <label className={labelClass}>Select File</label>
            <input
              type="file"
              onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
              required
              className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
            />
            {errors?.file && (
              <p className="text-hpi-red mt-1 text-sm">{errors.file[0]}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <a
              href={companyResourcesPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Uploading..." : "Add Resource"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
